import type { InatoCodec } from '@inato/codec';
import { Either, Option, Schema, pipe } from 'effect';
import { either, json } from 'fp-ts';
import { useCallback, useMemo, useState } from 'react';

import type { PersistentStorageService } from '../application/PersistentStorageService';
import { makeContext } from './shared/makeContext';

const { Provider, useContext: usePersistentStorageService } =
  makeContext<PersistentStorageService>('PersistentStorageService');

export const PersistentStorageServiceProvider = Provider;

const usePersistentStorageStateWithCodec = <
  T extends
    | string
    | number
    | boolean
    | Record<string, any>
    | ReadonlyArray<any>,
>(
  key: string,
  initialValue: T,
  codec: InatoCodec.InatoCodec<unknown, unknown, T>,
) => {
  const { getItem, setItem } = usePersistentStorageService();
  const [storedValue, setStoredValue] = useState(
    (): T =>
      pipe(
        getItem(key),
        Option.fromNullable,
        Option.map(item =>
          pipe(json.parse(item), either.flatMap(codec.decode)),
        ),
        Option.flatMap(either.match(Option.none, Option.some)),
        Option.getOrElse(() => {
          setItem(key, JSON.stringify(initialValue));
          return initialValue;
        }),
      ),
  );

  const setValue = useCallback(
    (value: T) => {
      pipe(codec.encode(value), encodedValue => {
        setItem(key, JSON.stringify(encodedValue));
        setStoredValue(value);
      });
    },
    [key, setItem, codec],
  );

  return [storedValue, setValue] as const;
};

const usePersistentStorageStateWithSchema = <A>(
  key: string,
  initialValue: NoInfer<A>,
  schema: Schema.Schema<A, string>,
): {
  value: A;
  setValue: (value: A) => void;
  setValueWithCallback: (mapStoredItem: (storedItem: A) => A) => void;
  clear: () => void;
} => {
  const { removeItem } = usePersistentStorageService();

  const setItem = useSafeSetItem(schema);
  const getItem = useSafeGetItem(schema);

  const [value, setValue_] = useState(
    (): A =>
      pipe(
        getItem(key),
        Option.getOrElse(() => {
          setItem(key, initialValue);
          return initialValue;
        }),
      ),
  );

  const setValue = useCallback(
    (value: A) => {
      setItem(key, value);
      setValue_(value);
    },
    [key, setItem],
  );

  const setValueWithCallback = useCallback(
    (mapStoredItem: (storedItem: A) => A) => {
      const newValue = pipe(
        getItem(key),
        Option.getOrElse(() => initialValue),
        mapStoredItem,
      );
      setItem(key, newValue);
      setValue_(newValue);
    },
    [getItem, initialValue, key, setItem],
  );

  const clear = useCallback(() => {
    removeItem(key);
  }, [key, removeItem]);

  return useMemo(
    () => ({ value, setValue, setValueWithCallback, clear }) as const,
    [clear, setValue, setValueWithCallback, value],
  );
};

export const usePersistentStorageState = {
  codec: usePersistentStorageStateWithCodec,
  schema: usePersistentStorageStateWithSchema,
};

const useSafeSetItem = <A>(schema: Schema.Schema<A, string>) => {
  const { setItem } = usePersistentStorageService();

  return useCallback(
    (key: string, value: A): void => {
      const encoded = Schema.encodeEither(schema)(value);
      if (Either.isLeft(encoded)) {
        return;
      }
      setItem(key, encoded.right);
    },
    [schema, setItem],
  );
};

const useSafeGetItem = <A>(schema: Schema.Schema<A, string>) => {
  const { getItem, removeItem } = usePersistentStorageService();
  const storageSchema = Schema.OptionFromNullOr(schema);

  return useCallback(
    (key: string): Option.Option<A> => {
      const item = getItem(key);
      const decoded = Schema.decodeEither(storageSchema)(item);
      if (Either.isLeft(decoded)) {
        removeItem(key);
        return Option.none();
      }
      return decoded.right;
    },
    [getItem, removeItem, storageSchema],
  );
};
